import { Refresh } from "@mui/icons-material"
import { Box, IconButton, Skeleton, Stack, Typography } from "@mui/material"
import { FC, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useGetSomumInfoQuery } from "src/features/partners/partnersApi"
import SaLayout from "./SaLayout"

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const SaSomumPage: FC = () => {
  const { t } = useTranslation()

  const qInfo = useGetSomumInfoQuery({})

  const qInfoIsLoading = qInfo.isLoading || qInfo.isFetching

  const refresh = useCallback(() => {
    qInfo.refetch()
  }, [qInfo])

  return (
    <SaLayout selectedMenuItemLabelKey="somum">
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                mt: 0.5,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
              }}
            >
              {t("somum")}
            </Typography>
          </Box>
          <IconButton
            onClick={refresh}
            size="small"
            disabled={qInfoIsLoading}
            sx={{
              bgcolor: "rgba(35, 66, 133, 0.06)",
              color: "primary.main",
              "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
            }}
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Stack>

        {/* Connection status card */}
        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "text.secondary",
              mb: 1.25,
            }}
          >
            {t("apiUrl")}
          </Typography>
          {qInfoIsLoading ? (
            <Skeleton variant="text" width={200} height={20} />
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
              <Typography
                variant="caption"
                fontFamily="monospace"
                sx={{
                  bgcolor: "rgba(35, 66, 133, 0.06)",
                  color: "text.primary",
                  borderRadius: 1,
                  px: 1,
                  py: 0.25,
                }}
              >
                {qInfo.data?.data?.somumApiUrl}
              </Typography>
              {qInfo.data?.data?.isOnline ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "green",
                    }}
                  />
                  <Box
                    sx={{
                      bgcolor: "rgba(76, 175, 80, 0.12)",
                      color: "success.main",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {t("online")}
                  </Box>
                  <Typography variant="caption">
                    {`${t("latency")}: ${qInfo.data?.data?.pingLatency}ms`}
                  </Typography>
                  <Typography variant="caption">
                    {`${t("version")}: ${qInfo.data?.data?.version}`}
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "red",
                    }}
                  />
                  <Box
                    sx={{
                      bgcolor: "rgba(211, 47, 47, 0.12)",
                      color: "error.main",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {t("offline")}
                  </Box>
                </Stack>
              )}
            </Stack>
          )}
        </Box>

        {/* Information card */}
        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "text.secondary",
              mb: 1.5,
            }}
          >
            {t("information")}
          </Typography>
          {qInfoIsLoading ? (
            <Skeleton variant="rectangular" width="100%" height={100} />
          ) : (
            <Stack direction="column" spacing={1}>
              <Typography variant="body2">
                <strong>{t("description")}:</strong>{" "}
                {qInfo.data?.data?.description}
              </Typography>
              <Typography variant="body2">
                <strong>{t("status")}:</strong> {qInfo.data?.data?.status}
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </SaLayout>
  )
}

export default SaSomumPage
